'use client';
import React from 'react';
import Cookies from 'js-cookie';
import Modal from '@repo/ui/src/modules/UI/components/Modal';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useLocalStorage } from '@hooks/useLocalStorage';
import useTranslation from '@components/Translation/useTranslation';
import Geo from '@models/geo';
import { CountryDetails } from './_CountryDetails';

const DISMISSED_LABEL = 'country-selector-dismissed';

export const CountryModal = () => {
  const { push, close } = useModal(ModalID.country);
  const { locale, geo } = useCurrentUrl();
  const { t } = useTranslation();
  const [hasDismissed, setDismissed, loading] = useLocalStorage(DISMISSED_LABEL, false, {
    useSessionStorage: true,
  });

  const usersGeo = React.useMemo(() => {
    const usersCountry = Cookies.get('geo');
    if (geo && usersCountry && !hasDismissed && !loading && geo?.productGeo !== usersCountry) {
      return new Geo({ productGeo: usersCountry });
    }
  }, [loading, hasDismissed, geo]);

  React.useEffect(() => {
    if (usersGeo?.exists && !hasDismissed) {
      push();
    }
  }, [hasDismissed, setDismissed, locale, loading]);

  return (
    <Modal
      id={ModalID.country}
      className="tw-p-15 tw-max-w-xl tw-grid tw-gap-y-6 md:tw-gapy-y-12 tw-justify-items-center"
      onClose={() => {
        setDismissed(true);
      }}
      aria-labelledby={`${ModalID.country}-modal-title`}
      data-testId={`${ModalID.country}-modal`}
    >
      <h1
        className="tw-text-white tw-grid tw-gap-y-2 tw-text-center tra-text-web-heading-4"
        id={`${ModalID.country}-modal-title`}
      >
        <span className="tw-font-display46">
          {t('countryPopupContent.whereAreYou', undefined, { default: 'Where are you' })}
        </span>
        <span>
          {t('countryPopupContent.shoppingFrom', undefined, { default: 'Shopping From' })}
        </span>
      </h1>
      <hr className="tw-h-1 tw-w-12 tw-bg-action-400 tw-border-none tw-block" />

      <p className="tw-text-white tw-text-center tra-text-web-body">
        {t('countryPopupContent.body', undefined, {
          default:
            'You’re currently in a different store than where your current location shows. Would you like to continue shopping here or select a different country?',
        })}
      </p>

      <div className="tw-grid tw-gap-y-3 md:tw-grid-cols-2 md:tw-gap-x-6">
        {usersGeo && geo && (
          <>
            <button
              aria-label="Close Modal"
              onClick={() => {
                setDismissed(true);
                close();
              }}
            >
              <CountryDetails {...geo} />
            </button>
            <a href={usersGeo.geoLoc}>
              <CountryDetails {...usersGeo} />
            </a>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CountryModal;
